import { useState, useEffect } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useStock } from "@/hooks/useStock";
import { StockAlerts } from "@/components/stock/StockAlerts";
import { IngredientsTab } from "@/components/stock/IngredientsTab";
import { RecipesTab } from "@/components/stock/RecipesTab";
import { MovementsTab } from "@/components/stock/MovementsTab";
import { CostsTab } from "@/components/stock/CostsTab";
import { IngredientForm } from "@/components/stock/IngredientForm";
import { RecipeForm } from "@/components/stock/RecipeForm";
import { EntryForm } from "@/components/stock/EntryForm";
import { WasteForm } from "@/components/stock/WasteForm";
import { AIRecipeWizard } from "@/components/stock/AIRecipeWizard";
import {
  Package, Plus, ShoppingCart, BarChart3, Utensils, Sparkles,
  ArrowDownCircle, ArrowUpCircle,
} from "lucide-react";

const TABS = [
  { id: "ingredients", label: "Ingredientes", icon: Package },
  { id: "recipes",     label: "Fichas Tecnicas", icon: Utensils },
  { id: "entries",     label: "Movimientos", icon: ShoppingCart },
  { id: "costs",       label: "Costes & Margem", icon: BarChart3 },
];

export default function StockPage() {
  const { authAxios, items, alerts, recipes, movements, costReport, products, loading, fetchAll, fetchCosts, deleteIngredient, deleteRecipe } = useStock();
  const [tab, setTab] = useState("ingredients");
  const [dialog, setDialog] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [showAIWizard, setShowAIWizard] = useState(false);

  useEffect(() => { if (tab === "costs") fetchCosts(); }, [tab, fetchCosts]);

  const tabCounts = { ingredients: items.length, recipes: recipes.length, entries: movements.length };

  const closeDialog = () => { setDialog(null); setEditItem(null); fetchAll(); };
  const openEdit = (type, item) => { setEditItem(item); setDialog(type); };

  if (loading) return <MainLayout><LoadingScreen /></MainLayout>;

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold uppercase tracking-tight" data-testid="stock-title">Inventario</h1>
            <p className="text-muted-foreground mt-1">Control de stock, fichas tecnicas y costes</p>
          </div>
          <div className="flex gap-2">
            {tab === "ingredients" && (
              <Button onClick={() => setDialog("ingredient")} className="gap-2" data-testid="new-ingredient-btn"><Plus className="w-4 h-4" />Ingrediente</Button>
            )}
            {tab === "recipes" && (
              <div className="flex gap-2">
                <Button onClick={() => setShowAIWizard(true)} variant="outline" className="gap-2 border-violet-400 text-violet-700 hover:bg-violet-50">
                  <Sparkles className="w-4 h-4" />Gerar com IA
                </Button>
                <Button onClick={() => setDialog("recipe")} className="gap-2" data-testid="new-recipe-btn"><Plus className="w-4 h-4" />Ficha Tecnica</Button>
              </div>
            )}
            {tab === "entries" && (
              <div className="flex gap-2">
                <Button onClick={() => setDialog("entry")} className="gap-2" data-testid="new-entry-btn"><ArrowUpCircle className="w-4 h-4" />Compra</Button>
                <Button onClick={() => setDialog("waste")} variant="outline" className="gap-2 text-red-500 border-red-500/30" data-testid="new-waste-btn"><ArrowDownCircle className="w-4 h-4" />Desperdicio</Button>
              </div>
            )}
          </div>
        </div>

        <StockAlerts alerts={alerts} />

        {/* Tabs */}
        <div className="flex gap-1 bg-secondary/50 rounded-lg p-1" data-testid="stock-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
              {tabCounts[t.id] !== undefined && <Badge variant="secondary" className="ml-1 text-xs">{tabCounts[t.id]}</Badge>}
            </button>
          ))}
        </div>

        {tab === "ingredients" && <IngredientsTab items={items} onEdit={item => openEdit("ingredient", item)} onDelete={deleteIngredient} />}
        {tab === "recipes"     && <RecipesTab recipes={recipes} onEdit={r => openEdit("recipe", r)} onDelete={deleteRecipe} />}
        {tab === "entries"     && <MovementsTab movements={movements} />}
        {tab === "costs"       && <CostsTab costReport={costReport} />}
      </div>

      {/* Dialogs */}
      <Dialog open={dialog === "ingredient"} onOpenChange={closeDialog}>
        <DialogContent><IngredientForm item={editItem} onClose={closeDialog} authAxios={authAxios} /></DialogContent>
      </Dialog>
      <Dialog open={dialog === "recipe"} onOpenChange={closeDialog}>
        <DialogContent className="max-w-lg"><RecipeForm products={products} stockItems={items} existingRecipe={editItem} onClose={closeDialog} authAxios={authAxios} /></DialogContent>
      </Dialog>
      <Dialog open={dialog === "entry"} onOpenChange={closeDialog}>
        <DialogContent><EntryForm stockItems={items} onClose={closeDialog} authAxios={authAxios} /></DialogContent>
      </Dialog>
      <Dialog open={dialog === "waste"} onOpenChange={closeDialog}>
        <DialogContent><WasteForm stockItems={items} onClose={closeDialog} authAxios={authAxios} /></DialogContent>
      </Dialog>

      <AIRecipeWizard
        open={showAIWizard}
        onClose={() => setShowAIWizard(false)}
        stockItems={items}
        onSaved={fetchAll}
      />
    </MainLayout>
  );
}
